import React, { useEffect } from "react";
import Select, { components as RSComponents } from "react-select";
import { HiSearch, HiX } from "react-icons/hi";

const BASE_HEIGHT = 32; // altura compacta p/ topbar

const styles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  control: (provided, state) => ({
    ...provided,
    minHeight: BASE_HEIGHT,
    height: BASE_HEIGHT,
    maxWidth: "320px",
    borderRadius: 9999,
    borderColor: state.isFocused ? "var(--primary, #111827)" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(17, 24, 39, 0.08)" : "none",
    backgroundColor: "#fff",
    ":hover": {
      borderColor: state.isFocused ? "var(--primary, #111827)" : "#d1d5db",
    },
    paddingLeft: 6, // espaço pro ícone
    cursor: "text",
    zIndex: 9999,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0 8px",
  }),
  input: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0,
    outline: "none",
    border: "none",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: BASE_HEIGHT,
  }),
  indicatorSeparator: () => ({ display: "none" }),
  placeholder: (provided) => ({
    ...provided,
    color: "#6b7280", // gray-500
    fontSize: 12,
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: 12,
    fontWeight: 600,
    color: "#111827", // gray-900
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 10px 20px rgba(0,0,0,0.08), 0 6px 8px rgba(0,0,0,0.04)",
    zIndex: 999999,
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: 13,
    fontWeight: state.isSelected ? 700 : 500,
    padding: "8px 12px",
    backgroundColor: state.isSelected
      ? "var(--primary, #111827)"
      : state.isFocused
      ? "rgba(17,24,39,0.08)"
      : "#fff",
    color: state.isSelected ? "#fff" : "#111827",
    ":active": {
      backgroundColor: state.isSelected
        ? "var(--primary, #111827)"
        : "rgba(17,24,39,0.12)",
    },
  }),
};

/** Ícone de busca (fixo à esquerda, não clicável) */
const SearchAdornment = (props) => {
  return (
    <div
      style={{
        position: "absolute",
        left: 10,
        top: "50%",
        transform: "translateY(-50%)",
        pointerEvents: "none",
        color: "#141414", // gray-400
        display: "flex",
        alignItems: "center",
      }}
      aria-hidden
    >
      <HiSearch size={16} />
    </div>
  );
};

/** Clear personalizado (um X arredondado bonitinho) */
const ClearIndicator = (props) => {
  const {
    innerProps: { ref, ...restInnerProps },
  } = props;
  return (
    <div
      ref={ref}
      {...restInnerProps}
      title="Limpar (Esc)"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: BASE_HEIGHT,
        width: 28,
        cursor: "pointer",
      }}
    >
      <HiX size={16} style={{ color: "#6b7280" }} />
    </div>
  );
};

/** Dropdown arrow minimalista */
const DropdownIndicator = (props) => {
  return (
    <RSComponents.DropdownIndicator {...props}>
      <svg
        width="12"
        height="12"
        viewBox="0 0 20 20"
        fill="none"
        style={{ color: "#141414" }}
      >
        <path
          d="M5 8l5 5 5-5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </RSComponents.DropdownIndicator>
  );
};

/**
 * ClientQuickFilter
 *
 * - Visual compacto (pill) para topbar
 * - Atalho '/' foca o campo
 * - Ícone de busca à esquerda
 * - Botão de limpar
 *
 * Props:
 * - options: [{ value, label }]
 * - value: string | number (id selecionado)
 * - onChange: (idOrEmpty: string) => void
 * - onClear?: () => void
 * - isLoading?: boolean
 * - placeholder?: string
 * - hotkey?: string (default '/')
 * - className?: string (wrapper)
 */
export default function ClientQuickFilter({
  options = [],
  value,
  onChange,
  onClear,
  isLoading = false,
  placeholder = "Buscar por cliente…",
  className = "",
}) {
  const ref = React.useRef(null);

  const selected =
    Array.isArray(options) &&
    options.find((o) => String(o.value) === String(value))?.value
      ? options.find((o) => String(o.value) === String(value))
      : null;

  return (
    <div className={`relative z-[60] ${className}`} style={{ minWidth: 260 }}>
      <SearchAdornment />

      <Select
        instanceId="client-quick-filter"
        inputId="client-quick-filter"
        isLoading={isLoading}
        options={options}
        value={selected}
        onChange={(opt) => onChange?.(opt?.value || "")}
        isClearable
        menuPortalTarget={typeof window !== "undefined" ? document.body : null}
        menuPosition="fixed"
        placeholder={placeholder}
        components={{
          DropdownIndicator,
          ClearIndicator,
          IndicatorSeparator: null,
        }}
        styles={styles}
        // passa o input ref do react-select
        // @ts-ignore - react-select não tipa ref direto em JS
        ref={ref}
        // acessibilidade
        aria-label="Buscar por cliente"
        noOptionsMessage={() => "Nenhum cliente encontrado"}
        // UX: abrir menu ao focar se já tem texto/valor
        openMenuOnFocus
        // melhorar busca interna
        filterOption={(opt, raw) =>
          opt.label?.toLowerCase().includes(raw.toLowerCase())
        }
      />
    </div>
  );
}
